/**
 * TopMenu.tsx - Top Navigation Menu Component
 * 
 * Universal horizontal top navigation with full OpCentral orchestrator integration
 * 
 * FEATURES:
 * - Horizontal top navigation bar
 * - Dropdown submenus
 * - Drag and drop reordering (Draggr)
 * - State persistence (Cachr)
 * - Action logging (Actr)
 * - Undo/redo support (Backtrackr)
 * - Plugin items integration
 * - Skinnr/Stylr theming
 * - Responsive (mobile menu)
 * 
 * ARCHITECTURE:
 * - Uses MenuItem component
 * - Uses useMenuState hook (Cachr/Actr/Backtrackr integrated)
 * - Uses MenuItemManager utility
 * - Uses @opcentral/draggr for drag and drop
 * 
 * CHANGE HISTORY:
 * - 251115_1730 [current] danielbeer: Created with orchestrator integration
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useMenuState } from './useMenuState';
import { MenuItemManager } from './MenuItemManager';
import { MenuItem } from './MenuItem';
import type { TopMenuProps, MenuItem as MenuItemType } from './types';

export const TopMenu: React.FC<TopMenuProps> = ({
  appName,
  items,
  theme = 'light',
  variant = 'default',
  persistState = true,
  search = false,
  badges: _badges = true, // TODO: Pass to MenuItem to control badge visibility
  shortcuts: _shortcuts = true, // TODO: Pass to MenuItem to control shortcut visibility
  draggable = false,
  onItemClick,
  onStateChange,
  onItemReorder,
  className = ''
}) => {
  // Menu state management (already has Cachr/Actr/Backtrackr)
  const {
    state,
    toggleItemExpanded,
    selectItem,
    reorderItems,
    setSearchQuery,
    clearSearch
  } = useMenuState({
    appName,
    menuId: 'top-menu',
    persistState,
    onStateChange
  });
  
  // Menu item manager
  const itemManager = useMemo(() => new MenuItemManager(items), [items]);
  
  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get filtered items based on search
  const filteredItems = useMemo(() => {
    if (!search || !state.searchQuery) {
      return itemManager.getVisibleItems();
    }
    return itemManager.search(state.searchQuery);
  }, [itemManager, search, state.searchQuery]);
  
  // Handle item click
  const handleItemClick = useCallback((item: MenuItemType) => {
    selectItem(item.id);
    
    if (item.type === 'submenu') {
      toggleItemExpanded(item.id);
    }
    
    // Close mobile menu on item click
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    
    onItemClick?.(item);
  }, [selectItem, toggleItemExpanded, mobileMenuOpen, onItemClick]);
  
  // Handle reorder
  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    const allItems = filteredItems;
    const itemIds = allItems.map(i => i.id);
    reorderItems(fromIndex, toIndex, itemIds);
    onItemReorder?.(fromIndex, toIndex);
  }, [filteredItems, reorderItems, onItemReorder]);
  
  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    setSearchQuery(value);
  }, [setSearchQuery]);
  
  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    clearSearch();
  }, [clearSearch]);
  
  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);
  
  // Menu classes
  const menuClasses = useMemo(() => [
    'top-menu',
    `top-menu--${theme}`,
    `top-menu--${variant}`,
    mobileMenuOpen ? 'top-menu--mobile-open' : '',
    draggable ? 'top-menu--draggable' : '',
    className
  ].filter(Boolean).join(' '), [theme, variant, mobileMenuOpen, draggable, className]);
  
  return (
    <nav 
      className={menuClasses}
      data-menu-id="top-menu"
      aria-label="Top navigation"
      role="navigation"
    >
      {/* Mobile menu toggle */}
      <button
        className="top-menu__mobile-toggle"
        onClick={toggleMobileMenu}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Menu content */}
      <div className="top-menu__content">
        {/* Search bar */}
        {search && (
          <div className="top-menu__search">
            <input
              type="text"
              className="top-menu__search-input"
              placeholder="Search..."
              value={searchInput}
              onChange={handleSearchChange}
              aria-label="Search menu items"
            />
            {searchInput && (
              <button
                className="top-menu__search-clear"
                onClick={handleSearchClear}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        )}
        
        {/* Menu items */}
        <div 
          className="top-menu__items"
          role="menubar"
        >
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className="top-menu__item-wrapper"
              role="none"
            >
              <MenuItem
                item={item}
                index={index}
                selected={state.selectedItem === item.id}
                expanded={state.expandedItems.includes(item.id)}
                draggable={draggable}
                onClick={() => handleItemClick(item)}
                onToggleExpanded={() => toggleItemExpanded(item.id)}
                onReorder={handleReorder}
              />
              
              {/* Dropdown submenu */}
              {item.type === 'submenu' && 
               state.expandedItems.includes(item.id) && 
               item.children && (
                <div 
                  className="top-menu__dropdown"
                  role="menu"
                  aria-label={`${item.label} submenu`}
                >
                  {item.children.map((child, childIndex) => (
                    <MenuItem
                      key={child.id}
                      item={child}
                      index={childIndex}
                      selected={state.selectedItem === child.id}
                      onClick={() => handleItemClick(child)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="top-menu__empty">
            {state.searchQuery ? (
              <p>No items match "{state.searchQuery}"</p>
            ) : (
              <p>No menu items</p>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

TopMenu.displayName = 'TopMenu';
